import jwtDecode from 'jwt-decode';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// ==================== TOKEN MANAGEMENT ====================
export const setToken = (token) => {
  localStorage.setItem('xpunters_token', token);
};

export const getToken = () => {
  return localStorage.getItem('xpunters_token');
};

export const removeToken = () => {
  localStorage.removeItem('xpunters_token');
  localStorage.removeItem('xpunters_user');
};

export const isTokenValid = () => {
  const token = getToken();
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};




export const getUserFromToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return {
      id: decoded?.data?.user?.id || decoded?.user?.id || null,
      ...decoded,
    };
  } catch {
    return null;
  }
};

// Get user ID from token
export const getUserIdFromToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded?.data?.user?.id || decoded?.user?.id || null;
  } catch {
    return null;
  }
};

export const setUserData = (data) => {
  localStorage.setItem('xpunters_user', JSON.stringify(data));
};

export const getUserData = () => {
  try {
    const data = localStorage.getItem('xpunters_user');
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// ==================== DATE FORMATTING ====================
export const formatDate = (date) => {
  if (!date) return 'N/A';
  return dayjs(date).format('MMM D, YYYY h:mm A');
};

export const formatTimeAgo = (date) => {
  if (!date) return 'N/A';
  return dayjs(date).fromNow();
};

export const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  return dayjs(date).fromNow();
};

export const isExpired = (date) => {
  if (!date) return true;
  return dayjs(date).isBefore(dayjs());
};

 

// ==================== USER HELPERS ====================
export const getUserId = () => {
  // Try from localStorage first
  const userData = getUserData();
  if (userData?.id) return userData.id;
  if (userData?.ID) return userData.ID;
  
  // Try from token
  return getUserIdFromToken();
};

// ==================== CURRENCY FORMATTING ====================
export const formatCurrency = (amount, currency = 'NGN', symbol = '₦') => {
  if (amount === undefined || amount === null) return `${symbol}0.00`;
  return `${symbol}${Number(amount).toFixed(2)}`;
};

export const formatXPUNT = (amount) => {
  if (amount === undefined || amount === null) return '0.00 $XPUNT';
  return `${Number(amount).toFixed(2)} $XPUNT`;
};

// ==================== BET CODE HELPERS ====================
export const getStatusColor = (status) => {
  const colors = {
    running: '#3498db',
    won: '#2ecc71',
    win: '#2ecc71',
    loose: '#e74c3c',
    lost: '#e74c3c',
    lose: '#e74c3c',
    loss: '#e74c3c',
    pending: '#f39c12',
    private: '#9b59b6',
    completed: '#95a5a6',
    refunded: '#e67e22',
    cancelled: '#c0392b',
  };
  return colors[status?.toLowerCase()] || '#95a5a6';
};

export const getResultEmoji = (result) => {
  if (result === 'won') return '🎉';
  if (result === 'loss') return '😢';
  return '⏳';
};

export const getConfidenceBadge = (confidence) => {
  const badges = {
    high: { label: '🔥 High', color: '#2ecc71' },
    medium: { label: '👍 Medium', color: '#f39c12' },
    low: { label: '👎 Low', color: '#e74c3c' },
  };
  return badges[confidence] || { label: 'Medium', color: '#95a5a6' };
};

// ==================== VALIDATION HELPERS ====================
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePhone = (phone) => {
  const re = /^\+?[\d\s-]{10,}$/;
  return re.test(phone);
};

export const validateAmount = (amount) => {
  return amount > 0 && !isNaN(amount);
};

// ==================== FILE HELPERS ====================
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const getFileType = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  if (imageTypes.includes(ext)) return 'image';
  return 'file';
};

// ==================== REFERRAL HELPERS ====================
export const generateReferralLink = (code, baseUrl = window.location.origin) => {
  return `${baseUrl}/register?ref=${code}`;
};

export const copyToClipboard = (text) => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback
  const textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
  return Promise.resolve();
};

// ==================== NOTIFICATION HELPERS ====================
export const getNotificationIcon = (type) => {
  const icons = {
    bet_marked: '📌',
    bet_code_created: '📢',
    prediction_result: '🎯',
    h2h_result: '🏆',
    competition_update: '📊',
    subscription_update: '📰',
    wallet_update: '💰',
    rollover_update: '🔄',
    referral_bonus: '🎁',
    creator_subscription: '⭐',
    private_bet_notification: '🔒',
    winner_notification: '👑',
  };
  return icons[type] || '📬';
};

// ==================== ARRAY HELPERS ====================
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

export const sortBy = (array, key, order = 'asc') => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (typeof aVal === 'string') {
      return order === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }
    return order === 'asc' ? aVal - bVal : bVal - aVal;
  });
};

export const paginate = (array, page, perPage) => {
  const start = (page - 1) * perPage;
  const end = start + perPage;
  return array.slice(start, end);
};


// Add to existing helpers.js

import { getExchangeRate } from './exchangeRate';

// Convert amount to $XPUNT with current rate
export const convertToXPUNT = async (amount, currencyCode = 'NGN') => {
  const rate = await getExchangeRate(currencyCode);
  return amount / rate;
};

// Convert amount from $XPUNT to local currency
export const convertFromXPUNT = async (amount, currencyCode = 'NGN') => {
  const rate = await getExchangeRate(currencyCode);
  return amount * rate;
};

// Format amount in local currency
export const formatLocalCurrency = (amount, currencyCode = 'NGN', symbol = '₦') => {
  return `${symbol}${Number(amount).toFixed(2)}`;
};


export const getBetResult = (bet) => {
  if (!bet) return null;

  // Primary contract
  if (bet.is_marked === true && bet.marked_result === 'won') return 'won';
  if (bet.is_marked === true && (bet.marked_result === 'loss' || bet.marked_result === 'lost')) return 'loss';

  // Fallback field names
  const raw = (bet.result || bet.status || bet.marked_result || '').toString().toLowerCase();
  if (raw === 'won' || raw === 'win' || raw === 'success') return 'won';
  if (raw === 'loss' || raw === 'lost' || raw === 'fail' || raw === 'failed') return 'loss';

  return 'pending';
};

export const getLastBet = (bets) => {
  if (!bets || bets.length === 0) return null;
  return [...bets].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
  )[bets.length - 1];
};